import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiX } from 'react-icons/fi';
import dependencyService from '../../services/dependencyService';
import projectService from '../../services/projectService';
import taskService from '../../services/taskService';

const DependencyEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [formData, setFormData] = useState({
    project_id: '',
    dependent_on_project_id: '',
    task_id: '',
    dependent_on_task_id: '',
    dependency_type: 'Finish-to-Start',
    entity_type: 'Task',
    status: 'Active',
    priority: 'Medium',
    lag_days: 0,
    lead_days: 0,
    description: '',
    external_reference: '',
    impact_level: 'Medium',
    expected_resolution_date: '',
    resolution_notes: ''
  });

  useEffect(() => {
    fetchDependency();
    fetchProjects();
    fetchTasks();
  }, [id]);

  const fetchDependency = async () => {
    try {
      const response = await dependencyService.getById(id);
      const dep = response.data;
      setFormData({
        project_id: dep.project_id || '',
        dependent_on_project_id: dep.dependent_on_project_id || '',
        task_id: dep.task_id || '',
        dependent_on_task_id: dep.dependent_on_task_id || '',
        dependency_type: dep.dependency_type || 'Finish-to-Start',
        entity_type: dep.entity_type || 'Task',
        status: dep.status || 'Active',
        priority: dep.priority || 'Medium',
        lag_days: dep.lag_days || 0,
        lead_days: dep.lead_days || 0,
        description: dep.description || '',
        external_reference: dep.external_reference || '',
        impact_level: dep.impact_level || 'Medium',
        expected_resolution_date: dep.expected_resolution_date ? dep.expected_resolution_date.split('T')[0] : '',
        resolution_notes: dep.resolution_notes || ''
      });
    } catch (error) {
      toast.error('Failed to fetch dependency');
      console.error('Error fetching dependency:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await projectService.getAllProjects();
      console.log("resppppp",response.projects)
      setProjects(response.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await taskService.getAllTasks();
            console.log("resppppp",response)

      setTasks(response.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      await dependencyService.update(id, formData);
      toast.success('Dependency updated successfully');
      navigate('/app/dependencies');
    } catch (error) {
      toast.error(error.error || 'Failed to update dependency');
      console.error('Error updating dependency:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Edit Dependency</h1>
          <p className="text-gray-600 mt-1">Update dependency information</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-6">
            {/* Entity Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Entity Type
              </label>
              <select
                name="entity_type"
                value={formData.entity_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="Project">Project</option>
                <option value="Task">Task</option>
                <option value="Milestone">Milestone</option>
                <option value="External">External</option>
              </select>
            </div>

            {/* Project Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project
              </label>
              <select
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name} ({project.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Task Selection */}
            {formData.entity_type === 'Task' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Task
                </label>
                <select
                  name="task_id"
                  value={formData.task_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a task</option>
                  {tasks.map(task => (
                    <option key={task.id} value={task.id}>
                      {task.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Dependency Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Dependency Type
                </label>
                <select
                  name="dependency_type"
                  value={formData.dependency_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Finish-to-Start">Finish-to-Start</option>
                  <option value="Start-to-Start">Start-to-Start</option>
                  <option value="Finish-to-Finish">Finish-to-Finish</option>
                  <option value="Start-to-Finish">Start-to-Finish</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Active">Active</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Blocked">Blocked</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Impact Level
                </label>
                <select
                  name="impact_level"
                  value={formData.impact_level}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="None">None</option>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lag Days
                </label>
                <input
                  type="number"
                  name="lag_days"
                  value={formData.lag_days}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lead Days
                </label>
                <input
                  type="number"
                  name="lead_days"
                  value={formData.lead_days}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Expected Resolution Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Resolution Date
              </label>
              <input
                type="date"
                name="expected_resolution_date"
                value={formData.expected_resolution_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Resolution Notes */}
            {formData.status === 'Resolved' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Resolution Notes
                </label>
                <textarea
                  name="resolution_notes"
                  value={formData.resolution_notes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/app/dependencies')}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
            >
              <FiX className="w-5 h-5" />
              <span>Cancel</span>
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <FiSave className="w-5 h-5" />
              <span>{saving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DependencyEdit;